import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DataSource } from '@/services/api/models/DataSource'
import type { DatabaseInfo } from '@/services/api/models/DatabaseInfo'
import { saveExpandedState, loadExpandedState } from '@/utils/explorerStateStorage'
import { useSettingsStore } from '@/stores/settingsStore'
import DatabaseFilterDialog from './DatabaseFilterDialog'
import { isDatabaseHidden, isSchemaHidden } from '@/utils/databaseFilterStorage'
// Use DatabaseInfo type for consistency with API
type Database = DatabaseInfo

interface Schema {
  name: string
  owner?: string
}

interface Table {
  name: string
  schema?: string
  type?: string
  engine?: string
  rows?: number
  size?: string  // Size as string with units (e.g., "0.01 MB")
}

// Updated Index interface to match AgentProxyGetIndexesResponse format
interface Index {
  name?: string
  table?: string
  schema?: string
  columns?: {
    name?: string
    ascending?: boolean
  }[]
  unique?: boolean
  type?: string
  primary?: boolean
  nullable?: boolean
  cardinality?: number
  definition?: string
}

interface DatabaseObject {
  name?: string
  description?: string
  type?: string
  arguments?: string
}

interface Trigger extends DatabaseObject {
  table?: string
  event?: string
  timing?: string
  statement?: string
}

interface Sequence extends DatabaseObject {
  data_type?: string
  start_value?: number
  increment?: number
  max_value?: number
  min_value?: number
  cache?: number
  cycle?: boolean
}

interface Extension extends DatabaseObject {
  version?: string
  schema?: string
  comment?: string
}

interface Event extends DatabaseObject {
  definer?: string
  time_zone?: string
  body?: string
  status?: string
  type?: string
}

// Type guard functions
function isDatabaseWithName(database: Database): database is Database & { name: string } {
  return typeof database.name === 'string' && database.name.length > 0
}

function isObjectWithName(obj: DatabaseObject): obj is DatabaseObject & { name: string } {
  return typeof obj.name === 'string' && obj.name.length > 0
}

function isIndexWithName(index: Index): index is Index & { name: string } {
  return typeof index.name === 'string' && index.name.length > 0
}

function isTableWithName(table: Table): table is Table & { name: string } {
  return table.name.length > 0
}

function isTrigger(obj: unknown): obj is Trigger {
  return typeof obj === 'object' && obj !== null && 
         ('name' in obj ? typeof obj.name === 'string' || obj.name === undefined : true)
}

function isSequence(obj: unknown): obj is Sequence {
  return typeof obj === 'object' && obj !== null && 
         ('name' in obj ? typeof obj.name === 'string' || obj.name === undefined : true)
}

function isExtension(obj: unknown): obj is Extension {
  return typeof obj === 'object' && obj !== null && 
         ('name' in obj ? typeof obj.name === 'string' || obj.name === undefined : true)
}

function isEvent(obj: unknown): obj is Event {
  return typeof obj === 'object' && obj !== null && 
         ('name' in obj ? typeof obj.name === 'string' || obj.name === undefined : true)
}
import { 
  useDatabasesQuery, 
  useSchemasQuery, 
  useTablesQuery,
  useViewsQuery,
  useFunctionsQuery,
  useProceduresQuery,
  useTriggersQuery,
  useSequencesQuery,
  useExtensionsQuery,
  useIndexesQuery,
  useEventsQuery,
  databaseKeys
} from '@/hooks/useDatabase'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import {
  ChevronRightIcon,
  ChevronDownIcon,
  CircleStackIcon,
  TableCellsIcon,
  EyeIcon,
  CodeBracketIcon,
  CogIcon,
  DocumentTextIcon,
  FolderIcon,
  FolderOpenIcon,
  ArrowPathIcon,
  BoltIcon,
  HashtagIcon,
  PuzzlePieceIcon,
  RectangleStackIcon,
  ClockIcon,
  PlusIcon,
  CheckCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { ContextMenu, type ContextMenuItem } from './ContextMenu'
import { 
  buildDatabaseContextMenu, 
  buildSchemaContextMenu, 
  buildTableContextMenu, 
  buildFolderContextMenu,
  buildViewContextMenu,
  buildFunctionContextMenu,
  buildProcedureContextMenu,
  buildIndexContextMenu,
  buildTriggerContextMenu,
  buildSequenceContextMenu,
  buildEventContextMenu,
  buildExtensionContextMenu,
  buildMultiSelectContextMenu,
  buildDatabasesRootContextMenu,
  type ContextMenuActionHandler
} from './contextMenuBuilders'

export interface RefreshData {
  type?: string
  database?: string
  schema?: string
  folderType?: string
}

type TreeItemType = 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'indexes-folder' | 'tables-folder' | 'views-folder' | 'functions-folder' | 'procedures-folder' | 'triggers-folder' | 'sequences-folder' | 'extensions-folder' | 'events-folder' | 'schemas-folder' | 'create-database' | 'create-table'

export interface DatabaseTreeRef {
  refresh: (data?: RefreshData) => void
  clearDatabaseExpansion: (databaseName: string) => void
  expandToNode: (database: string, schema?: string, nodeType?: string) => void
}

interface DatabaseTreeProps {
  connectionId: string
  dataSource: DataSource
  databaseType?: string
  selectedItem: { type: string; database: string; name: string; schema?: string; arguments?: string } | null
  onItemSelect: (item: {
    type: TreeItemType
    database: string
    name: string
    schema?: string
    arguments?: string
  }) => void
  onContextMenuAction?: ContextMenuActionHandler
  clearMultiSelection?: boolean // When true, clears the multi-selection
  analyzedTables?: Map<string, number> // Map of table keys to timestamps for visual indicators
}

interface TreeNode {
  id: string
  name: string 
  type: 'connection' | 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'folder' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'indexes-folder' | 'tables-folder' | 'views-folder' | 'functions-folder' | 'procedures-folder' | 'triggers-folder' | 'sequences-folder' | 'extensions-folder' | 'events-folder' | 'schemas-folder' | 'create-database' | 'create-table'
  children?: TreeNode[]
  database?: string
  schema?: string
  arguments?: string
  isLoading?: boolean
  hasChildren?: boolean
  rowCount?: number
}

export const DatabaseTree = forwardRef<DatabaseTreeRef, DatabaseTreeProps>(({ connectionId, dataSource, databaseType, selectedItem, onItemSelect, onContextMenuAction: _onContextMenuAction, clearMultiSelection, analyzedTables }, ref) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => loadExpandedState(connectionId))
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean
    x: number
    y: number
    items: ContextMenuItem[]
  }>({ visible: false, x: 0, y: 0, items: [] })
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const refreshCallbacksRef = useRef<Map<string, () => void>>(new Map())
  const queryClient = useQueryClient()
  const { settings } = useSettingsStore()
  
  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    saveExpandedState(connectionId, expandedNodes)
  }, [connectionId, expandedNodes])

  // Clear multi-selection when requested
  useEffect(() => {
    if (clearMultiSelection) {
      setSelectedItems(new Set())
      setLastSelectedId(null)
    }
  }, [clearMultiSelection])
  
  // Handle keyboard events for multi-selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedItems(new Set())
        setLastSelectedId(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
  
  const { 
    data: databases, 
    isLoading: isLoadingDatabases, 
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchDatabases 
  } = useDatabasesQuery(connectionId, { enrichDetails: false })
  
  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: (data?: RefreshData) => {
      console.log('DatabaseTree refresh called with:', data)
      
      if (!data) {
        // Refresh all databases
        console.log('Refreshing all databases')
        void queryClient.invalidateQueries({ queryKey: databaseKeys.list(connectionId) })
        void refetchDatabases()
        return
      }

      // Smart refresh based on what was clicked
      if (data.type === 'database' && data.database) {
        console.log('Refreshing database:', data.database)
        // For database refresh, refresh schemas (if PostgreSQL) and all expanded folders
        const dbId = `database-${data.database}`
        if (expandedNodes.has(dbId)) {
          // Refresh schemas for this database
          const schemasKey = `${data.database}-schemas`
          const schemasCallback = refreshCallbacksRef.current.get(schemasKey)
          if (schemasCallback) {
            console.log('Refreshing schemas for database')
            schemasCallback()
          }
          
          // Trigger refresh for all expanded folders under this database
          for (const [key, callback] of refreshCallbacksRef.current.entries()) {
            if (key.startsWith(`${data.database}-`) && !key.endsWith('-schemas')) {
              console.log('Refreshing key:', key)
              callback()
            }
          }
        }
      } else if (data.type === 'schema' && data.database && data.schema) {
        console.log('Refreshing schema:', data.schema)
        // For schema refresh, only refresh if the schema is expanded
        const schemaId = `schema-${data.database}-${data.schema}`
        if (expandedNodes.has(schemaId)) {
          // Trigger refresh for all expanded folders under this schema
          for (const [key, callback] of refreshCallbacksRef.current.entries()) {
            if (key.startsWith(`${data.database}-${data.schema}-`)) {
              console.log('Refreshing key:', key)
              callback()
            }
          }
        }
      } else if (data.type === 'folder' && data.folderType && data.database) {
        // For folder refresh, refresh the specific folder and cascade to expanded children
        // Extract the folder type name (e.g., 'tables' from 'tables-folder')
        const folderType = data.folderType.replace('-folder', '')
        
        // Special case for schemas - they don't use schema in the key (database-level)
        const folderId = folderType === 'schemas' 
          ? `${data.database}-schemas`
          : `${data.database}-${data.schema || 'default'}-${folderType}`
          
        console.log(`Looking for folder callback with key: ${folderId}`)
        const callback = refreshCallbacksRef.current.get(folderId)
        if (callback) {
          console.log(`Refreshing folder: ${folderType} with key: ${folderId}`)
          callback()
          
          // Cascading refresh for expanded children
          if (folderType === 'schemas') {
            // For schemas folder refresh, also refresh all expanded individual schemas and their folders
            const schemasId = `schemas-${data.database}`
            if (expandedNodes.has(schemasId)) {
              console.log('Cascading refresh to expanded schemas and their folders')
              // Find all expanded schemas under this database and refresh their folders
              for (const nodeId of expandedNodes) {
                if (nodeId.startsWith(`schema-${data.database}-`)) {
                  const schemaName = nodeId.replace(`schema-${data.database}-`, '')
                  console.log(`Found expanded schema: ${schemaName}`)
                  
                  // Refresh all folders under this expanded schema
                  for (const [key, callback] of refreshCallbacksRef.current.entries()) {
                    if (key.startsWith(`${data.database}-${schemaName}-`)) {
                      console.log('Cascading refresh to key:', key)
                      callback()
                    }
                  }
                }
              }
            }
          }
        } else {
          console.log(`No refresh callback found for key: ${folderId}`)
          console.log('Available keys:', Array.from(refreshCallbacksRef.current.keys()))
        }
      } else if (data.type === 'databases-root') {
        // Refresh the root databases list
        console.log('Refreshing databases root')
        void queryClient.invalidateQueries({ queryKey: databaseKeys.list(connectionId) })
        void refetchDatabases()
      } else if (data.type && ['table', 'view', 'function', 'procedure', 'trigger', 'index', 'event'].includes(data.type)) {
        // For individual objects, refresh their parent folder
        console.log(`Refreshing parent folder for ${data.type}:`, data)
        const folderMap: Record<string, string> = {
          'table': 'tables',
          'view': 'views',
          'function': 'functions',
          'procedure': 'procedures',
          'trigger': 'triggers',
          'index': 'indexes',
          'event': 'events'
        }
        const folderType = data.type ? folderMap[data.type] : undefined
        if (folderType && data.database) {
          const folderId = `${data.database}-${data.schema || 'default'}-${folderType}`
          const callback = refreshCallbacksRef.current.get(folderId)
          if (callback) {
            console.log(`Refreshing ${folderType} folder`)
            callback()
          }
        }
      }
    },
    clearDatabaseExpansion: (databaseName: string) => {
      setExpandedNodes(prev => {
        const next = new Set(prev)
        // Remove all expansion nodes related to this database
        const nodesToRemove: string[] = []
        for (const nodeId of next) {
          if (nodeId.startsWith(`database-${databaseName}`) ||
              nodeId.startsWith(`schema-${databaseName}-`) ||
              nodeId.startsWith(`schemas-${databaseName}`) ||
              nodeId.startsWith(`tables-${databaseName}-`) ||
              nodeId.startsWith(`views-${databaseName}-`) ||
              nodeId.startsWith(`functions-${databaseName}-`) ||
              nodeId.startsWith(`procedures-${databaseName}-`) ||
              nodeId.startsWith(`triggers-${databaseName}-`) ||
              nodeId.startsWith(`indexes-${databaseName}-`) ||
              nodeId.startsWith(`sequences-${databaseName}-`) ||
              nodeId.startsWith(`extensions-${databaseName}-`) ||
              nodeId.startsWith(`events-${databaseName}-`)) {
            nodesToRemove.push(nodeId)
          }
        }
        
        nodesToRemove.forEach(nodeId => next.delete(nodeId))
        return next
      })
      
      // Also clear any selected items for this database
      setSelectedItems(prev => {
        const next = new Set(prev)
        const itemsToRemove: string[] = []
        for (const itemId of next) {
          if (itemId.startsWith(`database-${databaseName}`) ||
              itemId.startsWith(`schema-${databaseName}-`) ||
              itemId.startsWith(`table-${databaseName}-`) ||
              itemId.startsWith(`view-${databaseName}-`) ||
              itemId.startsWith(`function-${databaseName}-`) ||
              itemId.startsWith(`procedure-${databaseName}-`) ||
              itemId.startsWith(`trigger-${databaseName}-`) ||
              itemId.startsWith(`index-${databaseName}-`) ||
              itemId.startsWith(`sequence-${databaseName}-`) ||
              itemId.startsWith(`extension-${databaseName}-`) ||
              itemId.startsWith(`event-${databaseName}-`)) {
            itemsToRemove.push(itemId)
          }
        }
        
        itemsToRemove.forEach(itemId => next.delete(itemId))
        return next
      })
    },
    expandToNode: (database: string, schema?: string, nodeType?: string) => {
      setExpandedNodes(prev => {
        const next = new Set(prev)
        // Expand database node
        next.add(`database-${database}`)
        
        // If schema is provided, expand schema-related nodes
        if (schema) {
          // For PostgreSQL, expand schemas folder and the specific schema
          if ((databaseType || dataSource.databaseType) === 'PostgreSQL') {
            next.add(`schemas-${database}`)  // Expand schemas folder
            next.add(`schema-${database}-${schema}`)  // Expand specific schema
          }
          
          // If a specific node type is provided (like 'views'), expand that folder
          if (nodeType) {
            next.add(`${nodeType}-${database}-${schema}`)
          }
        } else if (nodeType) {
          // For MySQL or when no schema, expand the folder directly under database
          next.add(`${nodeType}-${database}-default`)
        }
        
        return next
      })
    }
  }), [refetchDatabases, connectionId, queryClient, databaseType, dataSource.databaseType, expandedNodes])
  
  // Ensure databases is always an array and calculate filtered databases
  const databasesList = Array.isArray(databases) ? databases : []
  
  // Calculate visible databases count (after filtering)
  const dbTypeForFilter = (databaseType || dataSource?.databaseType) === 'PostgreSQL' ? 'postgresql' : 'mysql'
  const visibleDatabasesList = databasesList
    .filter(isDatabaseWithName)
    .filter((database) => !isDatabaseHidden(dataSource?.id || '', database.name, dbTypeForFilter))
  
  const visibleDatabasesCount = visibleDatabasesList.length

  // Handle scroll-based pagination with custom hook
  useInfiniteScroll(scrollRef as React.RefObject<HTMLElement>, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: () => { fetchNextPage() },
    threshold: 150, // Load more when within 150px of bottom
    debounceMs: 150 // Debounce scroll events by 150ms for better performance
  })

  const handleRefresh = useCallback(() => {
    void refetchDatabases()
  }, [refetchDatabases])

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        // When collapsing, check if we should keep nested items expanded
        next.delete(nodeId)
        
        // Only recursively collapse if the setting is disabled (default behavior)
        if (!settings.keepExplorerExpandedState) {
          // Helper function to determine if a node is nested under another
          const isNestedUnder = (childId: string, parentId: string): boolean => {
          // Extract the base components from the parent ID
          if (parentId.startsWith('database-')) {
            const dbName = parentId.substring('database-'.length)
            // Check if child belongs to this database
            return childId.includes(dbName) && childId !== parentId
          } else if (parentId.startsWith('schema-')) {
            // schema-{database}-{schema}
            const parts = parentId.substring('schema-'.length).split('-')
            const [dbName] = parts
            const schemaName = parts.slice(1).join('-') // Schema name might contain dashes
            // Check if child belongs to this schema
            return childId.includes(`${dbName}-${schemaName}`) && childId !== parentId
          } else if (parentId.startsWith('schemas-')) {
            // schemas-{database} folder
            const dbName = parentId.substring('schemas-'.length)
            // All schemas and their children under this database
            return (childId.startsWith(`schema-${dbName}-`) || 
                   childId.includes(`-${dbName}-`)) && 
                   childId !== parentId
          } else if (parentId.includes('-')) {
            // For folder nodes like tables-{database}-{schema}, views-{database}-{schema}, etc.
            // Also handles index-table-{database}-{schema}-{tableName}
            const parentParts = parentId.split('-')
            
            // Special case for index-table groups
            if (parentId.startsWith('index-table-')) {
              // index-table-{database}-{schema}-{tableName}
              // Children would be individual indexes under this table
              const [, , database, ...rest] = parentParts
              const tableName = rest.pop()
              const schema = rest.join('-') || 'default'
              return childId.startsWith(`index-${database}-${schema}-${tableName}-`)
            }
            
            // For regular folders (tables-, views-, functions-, etc.)
            // Extract database and schema from the parent
            const [folderType] = parentParts
            
            // Check if this is a folder under a schema/database
            if (['tables', 'views', 'functions', 'procedures', 'triggers', 'indexes', 'sequences', 'events'].includes(folderType)) {
              // No nested folders under these, so no children to collapse
              return false
            }
          }
          
          return false
        }
        
          // Remove all nested expanded nodes
          for (const expandedId of prev) {
            if (isNestedUnder(expandedId, nodeId)) {
              next.delete(expandedId)
            }
          }
        }
      } else {
        next.add(nodeId)
      }
      return next
    })
  }, [settings.keepExplorerExpandedState])

  const handleRangeSelection = useCallback((startId: string, endId: string) => {
    // Get all selectable elements from the DOM
    const selectableElements = scrollRef.current?.querySelectorAll('[data-selectable-id]')
    if (!selectableElements) {
      // Fallback to just selecting both items
      setSelectedItems(new Set([startId, endId]))
      return
    }
    
    const selectableIds: string[] = []
    for (const el of selectableElements) {
      if (el instanceof HTMLElement) {
        const id = el.dataset.selectableId
        if (typeof id === 'string' && id.length > 0) selectableIds.push(id)
      }
    }
    
    const startIndex = selectableIds.indexOf(startId)
    const endIndex = selectableIds.indexOf(endId)
    
    if (startIndex === -1 || endIndex === -1) {
      // Fallback to just selecting both items
      setSelectedItems(new Set([startId, endId]))
      return
    }
    
    // Select all items between start and end (inclusive)
    const minIndex = Math.min(startIndex, endIndex)
    const maxIndex = Math.max(startIndex, endIndex)
    const rangeItems = selectableIds.slice(minIndex, maxIndex + 1)
    
    setSelectedItems(new Set(rangeItems))
  }, [])

  const handleItemClick = useCallback((node: TreeNode, event?: React.MouseEvent) => {
    const isSelectable = ['database', 'schema', 'table', 'view', 'function', 'procedure', 'trigger', 'sequence', 'extension', 'index', 'event'].includes(node.type)
    
    if (isSelectable && event) {
      const isCtrlPressed = event.ctrlKey || event.metaKey
      const isShiftPressed = event.shiftKey
      
      if (isCtrlPressed) {
        // Toggle selection for Ctrl+click
        setSelectedItems(prev => {
          const next = new Set(prev)
          if (next.has(node.id)) {
            next.delete(node.id)
          } else {
            next.add(node.id)
          }
          return next
        })
        setLastSelectedId(node.id)
        // Don't expand when using Ctrl+click
        return
      } else if (isShiftPressed && lastSelectedId) {
        // Range selection for Shift+click
        handleRangeSelection(lastSelectedId, node.id)
        // Don't expand when using Shift+click
        return
      } else {
        // Single selection
        setSelectedItems(new Set([node.id]))
        setLastSelectedId(node.id)
      }
    } else {
      // Clear selection when clicking non-selectable items
      setSelectedItems(new Set())
      setLastSelectedId(null)
    }
    
    // Only toggle expansion if it's not a multi-selection action
    if (node.hasChildren || node.type === 'database' || node.type === 'schema' || node.type === 'folder') {
      toggleNode(node.id)
    }
    
    // Only call onItemSelect for actual database objects
    if (['database', 'schema', 'table', 'view', 'function', 'procedure', 'trigger', 'sequence', 'extension', 'index', 'event', 'indexes-folder', 'tables-folder', 'views-folder', 'functions-folder', 'procedures-folder', 'triggers-folder', 'sequences-folder', 'extensions-folder', 'events-folder', 'schemas-folder'].includes(node.type)) {
      onItemSelect({
        type: node.type as TreeItemType,
        database: node.database || node.name,
        name: node.name,
        schema: node.schema,
        ...(node.arguments ? { arguments: node.arguments } : {})
      })
    }
  }, [lastSelectedId, handleRangeSelection, toggleNode, onItemSelect])

  const getIcon = (type: TreeNode['type'], isExpanded: boolean) => {
    switch (type) {
      case 'connection':
        return CircleStackIcon
      case 'database':
        return CircleStackIcon
      case 'schema':
        return isExpanded ? FolderOpenIcon : FolderIcon
      case 'folder':
      case 'indexes-folder':
      case 'tables-folder':
      case 'views-folder':
      case 'functions-folder':
      case 'procedures-folder':
      case 'triggers-folder':
      case 'sequences-folder':
      case 'extensions-folder':
      case 'events-folder':
      case 'schemas-folder':
        return isExpanded ? FolderOpenIcon : FolderIcon
      case 'table':
        return TableCellsIcon
      case 'view':
        return EyeIcon
      case 'function':
        return CodeBracketIcon
      case 'procedure':
        return CogIcon
      case 'trigger':
        return BoltIcon
      case 'sequence':
        return HashtagIcon
      case 'extension':
        return PuzzlePieceIcon
      case 'index':
        return RectangleStackIcon
      case 'event':
        return ClockIcon
      case 'create-database':
      case 'create-table':
        return PlusIcon
      default:
        return DocumentTextIcon
    }
  }

  const DatabaseNode = ({ database }: { database: Database }) => {
    const dbId = `database-${database.name || 'unknown'}`
    const isExpanded = expandedNodes.has(dbId)
    const isPostgreSQL = (databaseType || dataSource.databaseType) === 'PostgreSQL'
    
    // Memoized handlers to avoid creating new functions in JSX
    const handleDatabaseClick = useCallback((e: React.MouseEvent) => {
      const databaseTreeNode: TreeNode = {
        id: dbId,
        name: database.name || 'unknown',
        type: 'database'
      }
      handleItemClick(databaseTreeNode, e)
    }, [dbId, database.name])

    const handleDatabaseContext = useCallback((e: React.MouseEvent) => {
      handleDatabaseContextMenu(e, database.name || '')
    }, [database.name])
    
    // Only fetch schemas for PostgreSQL and when expanded
    const { data: schemas, isLoading: isLoadingSchemas, refetch: refetchSchemas } = useSchemasQuery(
      connectionId, 
      database.name || '',
      { enabled: isExpanded && isPostgreSQL && isDatabaseWithName(database) }
    )
    
    // Register refetch callback for schemas
    useEffect(() => {
      if (isPostgreSQL && isDatabaseWithName(database)) {
        const key = `${database.name}-schemas`
        refreshCallbacksRef.current.set(key, () => void (async () => {
          console.log('Refreshing schemas for:', key)
          await queryClient.invalidateQueries({
            queryKey: databaseKeys.schemas(connectionId, database.name || '')
          })
          void refetchSchemas()
        })())
        return () => {
          refreshCallbacksRef.current.delete(key)
        }
      }
      return undefined
    }, [database, isPostgreSQL, refetchSchemas])
    
    // Early return after hooks to avoid conditional hook calls
    if (!isDatabaseWithName(database)) return null
    
    const schemasArray = Array.isArray(schemas) ? schemas : []
    
    const isSelected = selectedItem?.name === database.name && selectedItem.type === 'database'
    const isMultiSelected = selectedItems.has(dbId)
    const Icon = getIcon('database', isExpanded)

    return (
      <div>
        <div
          data-selectable-id={dbId}
          className={clsx(
            'flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group',
            (isSelected || isMultiSelected) && 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
            isMultiSelected && !isSelected && 'bg-blue-50 border border-blue-200 dark:bg-blue-900 dark:border-blue-700'
          )}
          onClick={handleDatabaseClick}
          onContextMenu={handleDatabaseContext}
        >
          <button className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100" type="button">
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-blue-600" />
          <span className="truncate font-medium">{database.name}</span>
          
          {database.size && (
            <span className="ml-auto text-xs text-gray-400">
              {database.size}
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isPostgreSQL ? (
              // PostgreSQL: Show Schemas folder and Extensions at database level
              <>
                <SchemasFolder 
                  database={database.name} 
                  connectionId={connectionId} 
                  schemas={schemasArray}
                  isLoadingSchemas={isLoadingSchemas}
                />
                <ExtensionsFolder 
                  database={database.name} 
                  connectionId={connectionId} 
                  databaseType={databaseType || dataSource.databaseType} 
                />
              </>
            ) : (
              // MySQL: Show table/view folders directly
              <>
                <TablesFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                <ViewsFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                <FunctionsFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                <ProceduresFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                <TriggersFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                <IndexesFolder database={database.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
                {(databaseType || dataSource.databaseType) === 'MySQL' && (
                  <EventsFolder database={database.name} connectionId={connectionId} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  const SchemasFolder = ({ database, connectionId, schemas, isLoadingSchemas }: { 
    database: string, 
    connectionId: string, 
    schemas: Schema[],
    isLoadingSchemas: boolean 
  }) => {
    const folderId = `schemas-${database}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)
    
    // Calculate visible schemas count (after filtering)
    const dbTypeForFilter = (databaseType || dataSource?.databaseType) === 'PostgreSQL' ? 'postgresql' : 'mysql'
    const visibleSchemas = schemas.filter((schema) => !isSchemaHidden(dataSource?.id || '', database, schema.name, dbTypeForFilter))
    const visibleSchemasCount = visibleSchemas.length

    const handleFolderClick = useCallback((e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('button')) {
        toggleNode(folderId)
      } else {
        toggleNode(folderId)
        handleItemClick({
          id: folderId,
          name: 'Schemas',
          type: 'schemas-folder' as TreeItemType,
          database
        })
      }
    }, [folderId, database])

    const handleFolderContext = useCallback((e: React.MouseEvent) => {
      handleFolderContextMenu(e, database, undefined, 'schemas-folder')
    }, [database])

    const handleToggleClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation()
      toggleNode(folderId)
    }, [folderId])

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={handleFolderContext}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={handleToggleClick}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-yellow-600" />
          <span className="truncate">Schemas</span>
          
          {!isExpanded && isLoadingSchemas && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingSchemas && visibleSchemasCount > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({visibleSchemasCount})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingSchemas ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading schemas...
              </div>
            ) : visibleSchemasCount === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">
                {schemas.length === 0 ? 'No schemas found' : 'All schemas are filtered'}
              </div>
            ) : (
              visibleSchemas
                .map((schema) => (
                  <SchemaNode 
                    key={schema.name} 
                    schema={schema} 
                    database={database}
                    connectionId={connectionId}
                  />
                ))
            )}
          </div>
        )}
      </div>
    )
  }

  const SchemaNode = ({ schema, database, connectionId }: { schema: Schema, database: string, connectionId: string }) => {
    const schemaId = `schema-${database}-${schema.name}`
    const isExpanded = expandedNodes.has(schemaId)
    const isSelected = selectedItem?.name === schema.name && selectedItem.type === 'schema'
    const isMultiSelected = selectedItems.has(schemaId)
    const Icon = getIcon('schema', isExpanded)
    
    const schemaTreeNode = useMemo(() => ({
      id: schemaId,
      name: schema.name,
      type: 'schema' as const,
      hasChildren: true,
      database,
      schema: schema.name
    }), [schemaId, schema.name, database])

    return (
      <div>
        <div
          data-selectable-id={schemaId}
          className={clsx(
            'flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group',
            (isSelected || isMultiSelected) && 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
            isMultiSelected && !isSelected && 'bg-blue-50 border border-blue-200 dark:bg-blue-900 dark:border-blue-700'
          )}
          onClick={(e) => handleItemClick(schemaTreeNode, e)}
          onContextMenu={(e) => { handleSchemaContextMenu(e, database, schema.name) }}
        >
          <button className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100" type="button">
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-yellow-600" />
          <span className="truncate">{schema.name}</span>
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            <TablesFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <ViewsFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <FunctionsFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <ProceduresFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <TriggersFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <IndexesFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
            <SequencesFolder database={database} schema={schema.name} connectionId={connectionId} databaseType={databaseType || dataSource.databaseType} />
          </div>
        )}
      </div>
    )
  }

  const TablesFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `tables-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    
    const { data: tablesResponse, isLoading: isLoadingTables, refetch: refetchTables } = useTablesQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType, limit: 1000 }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-tables`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing tables for:', key)
        // Invalidate the query to force a fresh fetch
        // Invalidate with partial matching to catch all variations (with page, limit, etc.)
        await queryClient.invalidateQueries({
          queryKey: databaseKeys.tables(connectionId, database, schema),
          exact: false
        })
        // Then refetch
        void refetchTables()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchTables, connectionId])
    
    const tablesArray: Table[] = Array.isArray(tablesResponse?.data) ? 
      (tablesResponse.data as unknown[]).filter((t): t is Table => {
        // Type guard to ensure the item is a valid Table object
        return typeof t === 'object' && t !== null && 
               ('name' in t ? typeof t.name === 'string' || t.name === undefined : true)
      }).filter((t) => {
      // Define what constitutes a view vs table more comprehensively
      const viewTypes = [
        'VIEW', 'VIEW', 'view', 'View',
        'MATERIALIZED VIEW', 'materialized view', 'MaterializedView'
      ];
      
      const systemTableTypes = [
        'SYSTEM TABLE', 'SYSTEM_TABLE', 'system_table', 'SystemTable',
        'SYSTEM TABLE', 'system table'
      ];
      
      const isView = t.type ? viewTypes.includes(t.type) : false;
      const isSystemTable = t.type ? systemTableTypes.includes(t.type) : false;
      
      // Accept as regular table if:
      // 1. No type specified (assume it's a table)
      // 2. Explicitly marked as TABLE or table
      // 3. Not a view or system table
      return !t.type || 
             t.type === 'BASE TABLE' || 
             t.type === 'TABLE' || 
             t.type === 'table' || 
             t.type === 'Table' ||
             t.type === 'BASE TABLE' ||
             t.type === 'base table' ||
             (!isView && !isSystemTable);
    }) : []
    
    const Icon = getIcon('folder', isExpanded)

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the tables folder itself
        handleItemClick({
          id: folderId,
          name: 'Tables',
          type: 'tables-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'tables-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-green-600" />
          <span className="truncate">Tables</span>
          
          {!isExpanded && isLoadingTables && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingTables && tablesArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({tablesArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingTables ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading tables...
              </div>
            ) : tablesArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No tables found</div>
            ) : (
              tablesArray.map((table) => (
                <TableNode 
                  key={`${database}-${schema || 'default'}-table-${table.name || 'unnamed'}`}
                  table={table} 
                  database={database}
                  schema={schema}
                  analyzedTables={analyzedTables}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const ViewsFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `views-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    
    const { data: views, isLoading: isLoadingViews, refetch: refetchViews } = useViewsQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-views`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing views for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'views', connectionId, database, schema],
          exact: false
        })
        void refetchViews()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchViews, connectionId])
    
    const viewsArray: Table[] = Array.isArray(views) ? views as Table[] : []
    
    const Icon = getIcon('folder', isExpanded)

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the views folder itself
        handleItemClick({
          id: folderId,
          name: 'Views',
          type: 'views-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'views-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-purple-600" />
          <span className="truncate">Views</span>
          
          {!isExpanded && isLoadingViews && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingViews && viewsArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({viewsArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingViews ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading views...
              </div>
            ) : viewsArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No views found</div>
            ) : (
              viewsArray.map((view) => {
                const tableWithType = { ...view, type: 'VIEW' }
                return (
                  <TableNode 
                    key={`${database}-${schema || 'default'}-view-${view.name || 'unnamed'}`}
                    table={tableWithType}
                    database={database}
                    schema={schema}
                    analyzedTables={analyzedTables}
                  />
                )
              })
            )}
          </div>
        )}
      </div>
    )
  }

  const FunctionsFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `functions-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: functions, isLoading: isLoadingFunctions, refetch: refetchFunctions } = useFunctionsQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-functions`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing functions for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'functions', connectionId, database, schema],
          exact: false
        })
        void refetchFunctions()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchFunctions, connectionId])
    
    const functionsArray: DatabaseObject[] = Array.isArray(functions) ? functions as DatabaseObject[] : []

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the functions folder itself
        handleItemClick({
          id: folderId,
          name: 'Functions',
          type: 'functions-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'functions-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-orange-600" />
          <span className="truncate">Functions</span>
          
          {!isExpanded && isLoadingFunctions && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingFunctions && functionsArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({functionsArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingFunctions ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading functions...
              </div>
            ) : functionsArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No functions found</div>
            ) : (
              functionsArray.map((func) => (
                <ObjectNode 
                  key={`${database}-${schema || 'default'}-function-${func.name || 'unnamed'}-${func.arguments || ''}`}
                  object={func} 
                  type="function"
                  database={database}
                  schema={schema}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const ProceduresFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `procedures-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: procedures, isLoading: isLoadingProcedures, refetch: refetchProcedures } = useProceduresQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-procedures`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing procedures for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'procedures', connectionId, database, schema],
          exact: false
        })
        void refetchProcedures()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchProcedures, connectionId])
    
    const proceduresArray: DatabaseObject[] = Array.isArray(procedures) ? procedures as DatabaseObject[] : []

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the procedures folder itself
        handleItemClick({
          id: folderId,
          name: 'Procedures',
          type: 'procedures-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'procedures-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-red-600" />
          <span className="truncate">Procedures</span>
          
          {!isExpanded && isLoadingProcedures && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingProcedures && proceduresArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({proceduresArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingProcedures ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading procedures...
              </div>
            ) : proceduresArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No procedures found</div>
            ) : (
              proceduresArray.map((proc) => (
                <ObjectNode 
                  key={`${database}-${schema || 'default'}-procedure-${proc.name || 'unnamed'}`}
                  object={proc} 
                  type="procedure"
                  database={database}
                  schema={schema}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const ObjectNode = ({ object, type, database, schema }: { object: DatabaseObject, type: TreeNode['type'], database: string, schema?: string }) => {
    if (!isObjectWithName(object)) return null
    const objectId = type === 'function' && object.arguments 
      ? `${type}-${database}-${schema || 'default'}-${object.name}-${object.arguments}`
      : `${type}-${database}-${schema || 'default'}-${object.name}`
    const isSelected = selectedItem?.name === object.name && 
                      selectedItem.database === database && 
                      selectedItem.schema === schema && 
                      selectedItem.type === type &&
                      (type !== 'function' || selectedItem.arguments === object.arguments)
    const isMultiSelected = selectedItems.has(objectId)
    const Icon = getIcon(type, false)

    const handleSelectClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      handleItemClick({
        id: objectId,
        name: object.name,
        type: type as 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'indexes-folder' | 'tables-folder' | 'views-folder' | 'functions-folder' | 'procedures-folder' | 'triggers-folder' | 'sequences-folder' | 'extensions-folder' | 'events-folder' | 'schemas-folder' | 'create-database' | 'create-table',
        database,
        schema,
        ...(type === 'function' && object.arguments ? { arguments: object.arguments } : {})
      }, e)
    }

    return (
      <div>
        <div
          data-selectable-id={objectId}
          className={clsx(
            'flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group',
            (isSelected || isMultiSelected) && 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
            isMultiSelected && !isSelected && 'bg-blue-50 border border-blue-200 dark:bg-blue-900 dark:border-blue-700'
          )}
          onClick={handleSelectClick}
          onContextMenu={(e) => { handleContextMenu(e, type, database, schema, object.name, type === 'function' ? object.arguments : undefined) }}
        >
          <div className="w-5"></div>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-gray-600" />
          <span className="truncate">{object.name}</span>
          
          {object.description && (
            <span className="ml-auto text-xs text-gray-400 truncate max-w-24">
              {object.description}
            </span>
          )}
        </div>
      </div>
    )
  }

  const TriggersFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `triggers-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: triggers, isLoading: isLoadingTriggers, refetch: refetchTriggers } = useTriggersQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-triggers`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing triggers for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'triggers', connectionId, database, schema],
          exact: false
        })
        void refetchTriggers()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchTriggers, connectionId])
    
    const triggersArray: Trigger[] = Array.isArray(triggers) ? 
      (triggers as unknown[]).filter(isTrigger) : []

    // Helper function to check if value is a record
    const isRecord = (value: unknown): value is Record<string, unknown> => {
      return typeof value === 'object' && value !== null && !Array.isArray(value)
    }

    // Normalize triggers to ensure a displayable name exists
    const normalizedTriggers: Trigger[] = triggersArray.map((t) => {
      let displayName: string | undefined
      const tAsRecord = t as unknown as Record<string, unknown>
      if (isRecord(tAsRecord)) {
        const candidateKeys = ['name','trigger_name','TRIGGER_NAME','triggerName']
        for (const key of candidateKeys) {
          const v = tAsRecord[key]
          if (typeof v === 'string') { displayName = v; break }
        }
      }
      return { ...t, name: displayName || 'Unnamed Trigger' }
    })

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the triggers folder itself
        handleItemClick({
          id: folderId,
          name: 'Triggers',
          type: 'triggers-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'triggers-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-yellow-600" />
          <span className="truncate">Triggers</span>
          
          {!isExpanded && isLoadingTriggers && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingTriggers && normalizedTriggers.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({normalizedTriggers.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingTriggers ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading triggers...
              </div>
            ) : normalizedTriggers.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No triggers found</div>
            ) : (
              normalizedTriggers.map((trigger, index) => (
                <ObjectNode 
                  key={`${database}-${schema || 'default'}-trigger-${trigger.name ?? 'unnamed'}-${index}`}
                  object={trigger} 
                  type="trigger"
                  database={database}
                  schema={schema}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const IndexesFolder = ({ database, schema, connectionId, databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `indexes-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: indexesResponse, isLoading: isLoadingIndexes, refetch: refetchIndexes } = useIndexesQuery(
      connectionId, 
      database, 
      schema,
      { enabled: isExpanded, databaseType, limit: 1000 }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-indexes`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing indexes for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'indexes', connectionId, database, schema],
          exact: false
        })
        void refetchIndexes()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchIndexes, connectionId])
    
    const indexesArray: Index[] = Array.isArray(indexesResponse?.data)
      ? (indexesResponse.data as unknown[]).filter((i): i is Index => !!i && typeof i === 'object')
      : []
    
    // Group indexes by table for DBeaver-like display
    const groupedIndexes: Record<string, Index[]> = {}
    for (const index of indexesArray) {
      const tableName = index.table || 'Unknown Table'
      if (!(tableName in groupedIndexes)) {
        groupedIndexes[tableName] = []
      }
      groupedIndexes[tableName].push(index)
    }

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the indexes folder itself
        handleItemClick({
          id: folderId,
          name: 'Indexes',
          type: 'indexes-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'indexes-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-indigo-600" />
          <span className="truncate">Indexes</span>
          
          {!isExpanded && isLoadingIndexes && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingIndexes && indexesArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({indexesArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingIndexes ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading indexes...
              </div>
            ) : indexesArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No indexes found</div>
            ) : (
              Object.entries(groupedIndexes).map(([tableName, tableIndexes]) => (
                <IndexTableGroup 
                  key={tableName}
                  tableName={tableName}
                  indexes={tableIndexes}
                  database={database}
                  schema={schema}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  // New component to display indexes grouped by table
  const IndexTableGroup = ({ tableName, indexes, database, schema }: { 
    tableName: string, 
    indexes: Index[], 
    database: string, 
    schema?: string 
  }) => {
    const groupId = `index-table-${database}-${schema || 'default'}-${tableName}`
    const isExpanded = expandedNodes.has(groupId)

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={() => { toggleNode(groupId) }}
        >
          <button className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100" type="button">
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <TableCellsIcon className="w-4 h-4 mr-2 flex-shrink-0 text-blue-600" />
          <span className="truncate">{tableName}</span>
          
          {!isExpanded && (
            <span className="ml-auto text-xs text-gray-400">
              ({indexes.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {indexes.map((index) => (
              <IndexNode 
                key={`${index.table || 'unknown'}-${index.name}`} 
                index={index} 
                database={database}
                schema={schema}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Enhanced IndexNode component with DBeaver-like details
  const IndexNode = ({ index, database, schema }: { index: Index, database: string, schema?: string }) => {
    if (!isIndexWithName(index)) return null
    const indexId = `index-${database}-${schema || 'default'}-${index.table}-${index.name}`
    const isSelected = selectedItem?.name === index.name && selectedItem.database === database && selectedItem.schema === schema && selectedItem.type === 'index'
    const isMultiSelected = selectedItems.has(indexId)
    const isExpandable = index.columns && Array.isArray(index.columns) && index.columns.length > 0
    const isExpanded = isExpandable && expandedNodes.has(indexId)

    const handleNodeClick = (e: React.MouseEvent) => {
      const isCtrlPressed = e.ctrlKey || e.metaKey
      const isShiftPressed = e.shiftKey
      
      if (isExpandable && !isCtrlPressed && !isShiftPressed) {
        e.stopPropagation()
        toggleNode(indexId)
      } else {
        handleItemClick({
          id: indexId,
          name: index.name,
          type: 'index',
          database,
          schema
        }, e)
      }
    }

    const handleSelectClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      handleItemClick({
        id: indexId,
        name: index.name,
        type: 'index',
        database,
        schema
      }, e)
    }

    const getIndexIcon = () => {
      if (index.primary) {
        return <div className="w-4 h-4 mr-2 flex-shrink-0 text-yellow-600 font-bold text-xs flex items-center justify-center">🔑</div>
      }
      if (index.unique) {
        return <div className="w-4 h-4 mr-2 flex-shrink-0 text-green-600 font-bold text-xs flex items-center justify-center">U</div>
      }
      return <HashtagIcon className="w-4 h-4 mr-2 flex-shrink-0 text-gray-600" />
    }

    return (
      <div>
        <div
          data-selectable-id={indexId}
          className={clsx(
            'flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group',
            (isSelected || isMultiSelected) && 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
            isMultiSelected && !isSelected && 'bg-blue-50 border border-blue-200 dark:bg-blue-900 dark:border-blue-700'
          )}
          onClick={handleNodeClick}
          onContextMenu={(e) => { handleContextMenu(e, 'index', database, schema, index.name) }}
        >
          {isExpandable ? (
            <button className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100" type="button">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-5"></div>
          )}
          
          {getIndexIcon()}
          <span className="truncate font-medium">{index.name}</span>
          
          {/* Enhanced index information display */}
          <div className="ml-auto flex items-center space-x-2">
            {/* Index type and properties */}
            <div className="flex items-center space-x-1">
              {index.primary && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-medium">PRIMARY</span>
              )}
              {index.unique && !index.primary && (
                <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded font-medium">UNIQUE</span>
              )}
              {index.type && (
                <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{index.type}</span>
              )}
            </div>
            
            {/* Column count and cardinality */}
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              {isExpandable && !isExpanded && index.columns && (
                <span>({index.columns.length} col{index.columns.length !== 1 ? 's' : ''})</span>
              )}
              {index.cardinality !== undefined && (
                <span title="Cardinality">Card: {index.cardinality}</span>
              )}
            </div>
            
            {/* Select button for expandable indexes */}
            {isExpandable && (
              <button type="button"
                onClick={handleSelectClick}
                className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100 transition-all"
                title="Select index"
              >
                <EyeIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Enhanced column display when expanded */}
        {isExpandable && isExpanded && index.columns && (
          <div className="ml-8 border-l border-gray-200 dark:border-gray-600 pl-4">
            <div className="text-xs text-gray-500 mb-1 px-2">Columns:</div>
            {index.columns.map((column: { name?: string; ascending?: boolean } | string, columnIndex: number) => {
              const columnName = typeof column === 'string' ? column : column.name || '';
              const isAscending = typeof column === 'object' ? column.ascending : undefined;
              
              return (
                <div
                  key={columnIndex}
                  className="flex items-center py-1 px-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded"
                >
                  <TableCellsIcon className="w-3 h-3 mr-2 flex-shrink-0 text-gray-400" />
                  <span className="truncate font-mono">{columnName}</span>
                  <div className="ml-auto flex items-center space-x-1">
                    {columnIndex === 0 && index.columns && index.columns.length > 1 && (
                      <span className="text-xs text-blue-600">(primary sort)</span>
                    )}
                    {isAscending !== undefined && (
                      <span className="text-xs text-gray-500">
                        {isAscending ? '↑ ASC' : '↓ DESC'}
                      </span>
                    )}
                    {index.nullable !== undefined && (
                      <span className="text-xs text-gray-500">
                        {index.nullable ? 'NULL' : 'NOT NULL'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Additional index metadata */}
            {(index.definition || index.cardinality !== undefined) && (
              <div className="mt-2 px-2 py-1 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                {index.cardinality !== undefined && (
                  <div className="text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Cardinality:</span> {index.cardinality}
                  </div>
                )}
                {index.definition && (
                  <div className="text-gray-600 dark:text-gray-400 mt-1">
                    <span className="font-medium">Definition:</span>
                    <pre className="text-xs mt-1 whitespace-pre-wrap font-mono bg-gray-100 dark:bg-gray-700 p-1 rounded">
                      {index.definition}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const SequencesFolder = ({ database, schema, connectionId, databaseType: _databaseType }: { database: string, schema?: string, connectionId: string, databaseType?: string }) => {
    const folderId = `sequences-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: sequences, isLoading: isLoadingSequences, refetch: refetchSequences } = useSequencesQuery(
      connectionId, 
      database, 
      schema,
      dataSource.databaseType === 'PostgreSQL',
      { enabled: isExpanded }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-sequences`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing sequences for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'sequences', connectionId, database, schema],
          exact: false
        })
        void refetchSequences()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchSequences, connectionId])
    
    const sequencesArray: Sequence[] = Array.isArray(sequences) ? 
      (sequences as unknown[]).filter(isSequence) : []

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the sequences folder itself
        handleItemClick({
          id: folderId,
          name: 'Sequences',
          type: 'sequences-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'sequences-folder') }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-pink-600" />
          <span className="truncate">Sequences</span>
          
          {!isExpanded && isLoadingSequences && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingSequences && sequencesArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({sequencesArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingSequences ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading sequences...
              </div>
            ) : sequencesArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No sequences found</div>
            ) : (
              sequencesArray.map((sequence) => (
                <ObjectNode 
                  key={`${database}-${schema || 'default'}-sequence-${sequence.name ?? 'unnamed'}`} 
                  object={sequence} 
                  type="sequence"
                  database={database}
                  schema={schema}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const ExtensionsFolder = ({ database, connectionId, databaseType: _databaseType }: { database: string, connectionId: string, databaseType?: string }) => {
    const folderId = `extensions-${database}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: extensions, isLoading: isLoadingExtensions, refetch: refetchExtensions } = useExtensionsQuery(
      connectionId, 
      database,
      dataSource.databaseType === 'PostgreSQL',
      { enabled: isExpanded }
    )
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-extensions`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing extensions for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'extensions', connectionId, database],
          exact: false
        })
        void refetchExtensions()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, refetchExtensions, connectionId])
    
    const extensionsArray: Extension[] = Array.isArray(extensions) ? 
      (extensions as unknown[]).filter(isExtension) : []

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the extensions folder itself
        handleItemClick({
          id: folderId,
          name: 'Extensions',
          type: 'extensions-folder' as TreeItemType,
          database
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, undefined, 'extensions-folder'); }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-teal-600" />
          <span className="truncate">Extensions</span>
          
          {!isExpanded && isLoadingExtensions && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingExtensions && extensionsArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({extensionsArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingExtensions ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading extensions...
              </div>
            ) : extensionsArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No extensions found</div>
            ) : (
              extensionsArray.map((extension) => (
                <ObjectNode 
                  key={`${database}-extension-${extension.name ?? 'unnamed'}`} 
                  object={extension} 
                  type="extension"
                  database={database}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const EventsFolder = ({ database, schema, connectionId }: { database: string, schema?: string, connectionId: string }) => {
    const folderId = `events-${database}-${schema || 'default'}`
    const isExpanded = expandedNodes.has(folderId)
    const Icon = getIcon('folder', isExpanded)

    const { data: events, isLoading: isLoadingEvents, refetch: refetchEvents } = useEventsQuery(
      connectionId, 
      database,
      dataSource.databaseType === 'MySQL',
      { enabled: isExpanded }
    )
    
    const eventsArray: Event[] = Array.isArray(events) ? 
      (events as unknown[]).filter(isEvent) : []
    
    // Register refetch callback
    useEffect(() => {
      const key = `${database}-${schema || 'default'}-events`
      refreshCallbacksRef.current.set(key, () => void (async () => {
        console.log('Refreshing events for:', key)
        await queryClient.invalidateQueries({
          queryKey: [...databaseKeys.all, 'events', connectionId, database, schema],
          exact: false
        })
        void refetchEvents()
      })())
      return () => {
        refreshCallbacksRef.current.delete(key)
      }
    }, [database, schema, refetchEvents, connectionId])

    const handleFolderClick = (e: React.MouseEvent) => {
      // Check if the click is on the expand button
      const { target } = e
      if (target instanceof Element && typeof target.closest === 'function' && target.closest('button')) {
        toggleNode(folderId)
      } else {
        // Toggle expansion and select the folder
        toggleNode(folderId)
        // Select the events folder itself
        handleItemClick({
          id: folderId,
          name: 'Events',
          type: 'events-folder' as TreeItemType,
          database,
          schema
        })
      }
    }

    return (
      <div>
        <div
          className="flex items-center py-1.5 px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
          onClick={handleFolderClick}
          onContextMenu={(e) => { handleContextMenu(e, 'folder', database, schema, 'events-folder'); }}
        >
          <button type="button" 
            className="mr-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation()
              toggleNode(folderId)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
          
          <Icon className="w-4 h-4 mr-2 flex-shrink-0 text-blue-600" />
          <span className="truncate">Events</span>
          
          {!isExpanded && isLoadingEvents && (
            <span className="ml-auto text-xs text-gray-400">Loading...</span>
          )}
          {!isExpanded && !isLoadingEvents && eventsArray.length > 0 && (
            <span className="ml-auto text-xs text-gray-400">
              ({eventsArray.length})
            </span>
          )}
        </div>
        
        {isExpanded && (
          <div className="ml-4">
            {isLoadingEvents ? (
              <div className="flex items-center py-1 px-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-transparent mr-2"></div>
                Loading events...
              </div>
            ) : eventsArray.length === 0 ? (
              <div className="py-1 px-2 text-sm text-gray-500">No events found</div>
            ) : (
              eventsArray.map((event) => (
                <ObjectNode 
                  key={`${database}-event-${event.name ?? 'unnamed'}`} 
                  object={event} 
                  type="event"
                  database={database}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }


  const TableNode = ({ table, database, schema, analyzedTables }: { table: Table, database: string, schema?: string, analyzedTables?: Map<string, number> }) => {
    if (!isTableWithName(table)) return null
    const isView = table.type === 'VIEW'
    const tableId = getItemId(isView ? 'view' : 'table', database, schema, table.name)
    const isSelected = selectedItem?.name === table.name && selectedItem.database === database && selectedItem.schema === schema
    const isMultiSelected = selectedItems.has(tableId)
    const Icon = getIcon(isView ? 'view' : 'table', false)

    // Helper function to format size for better readability
    const formatSize = (size: string | undefined) => {
      if (!size) return null
      
      // Extract numeric value and unit
      const match = /^([\d.]+)\s*(\w+)$/.exec(size)
      if (!match) return size
      
      const [, value, unit] = match
      const numValue = Number.parseFloat(value)
      
      // Convert to a more readable format
      if (unit.toLowerCase() === 'mb' && numValue < 1) {
        return `${(numValue * 1024).toFixed(0)} KB`
      }
      
      if (numValue < 0.01) {
        return '<0.01 MB'
      }
      
      return `${numValue.toFixed(2)} ${unit.toUpperCase()}`
    }

    const hasMetrics = (table.rows !== undefined) || (table.size !== undefined)
    const formattedSize = formatSize(table.size)
    const formattedRows = (table.rows !== undefined && table.rows !== null) ? table.rows.toLocaleString() : null
    
    // Check if this table was recently analyzed
    const tableKey = schema ? `${database}.${schema}.${table.name}` : `${database}.${table.name}`
    const isRecentlyAnalyzed = analyzedTables?.has(tableKey) || false

    return (
      <div
        data-selectable-id={tableId}
        className={clsx(
          'flex items-center px-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group',
          settings.explorerCompactView ? 'py-1' : 'py-1.5',
          (isSelected || isMultiSelected) && 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
          isMultiSelected && !isSelected && 'bg-blue-50 border border-blue-200 dark:bg-blue-900 dark:border-blue-700'
        )}
        onClick={(e) => {
          handleItemClick({
            id: tableId,
            name: table.name,
            type: isView ? 'view' : 'table',
            database,
            schema
          }, e)
          
          // Also call the original onItemSelect for compatibility
          onItemSelect({
            type: isView ? 'view' : 'table',
            database,
            name: table.name,
            schema,
          })
        }}
        onContextMenu={(e) => { handleContextMenu(e, isView ? 'view' : 'table', database, schema, table.name) }}
      >
        <div className="w-5 mr-1" /> {/* Spacer for alignment */}
        
        <Icon className={clsx(
          "w-4 h-4 mr-2 flex-shrink-0",
          isView ? 'text-purple-600' : 'text-green-600'
        )} />
        
        <div className="flex-1 min-w-0">
          <span className="truncate block">{table.name}</span>
          {hasMetrics && !settings.explorerCompactView && (
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
              {formattedRows !== null && (
                <span className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-1"></span>
                  {formattedRows} rows
                </span>
              )}
              {formattedSize && (
                <span className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-1"></span>
                  {formattedSize}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Analyze success indicator - positioned on the right side */}
        {isRecentlyAnalyzed && (
          <div className="ml-2 flex items-center">
            <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 animate-pulse" />
          </div>
        )}
        
        {/* Size indicator bar for visual comparison */}
        {table.size && !settings.explorerCompactView && (
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-12 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
              <div 
                className={clsx(
                  "h-full rounded-full transition-all",
                  (() => {
                    const sizeValue = table.size ? Number.parseFloat(table.size.split(' ')[0]) : 0;
                    return {
                      'bg-green-400': sizeValue < 1,
                      'bg-yellow-400': sizeValue >= 1 && sizeValue < 10,
                      'bg-orange-400': sizeValue >= 10 && sizeValue < 100,
                      'bg-red-400': sizeValue >= 100,
                    };
                  })()
                )}
                style={{
                  width: `${Math.min(100, Math.max(5, Math.log10((table.size ? Number.parseFloat(table.size.split(' ')[0]) : 1)) * 20 + 20))}%`
                }}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const getItemId = useCallback((type: string, database?: string, schema?: string, itemName?: string, functionArguments?: string): string => {
    if (itemName) {
      // For functions with arguments, include them in the ID for uniqueness
      if (type === 'function' && functionArguments) {
        return `${type}-${database}-${schema || 'default'}-${itemName}-${functionArguments}`
      }
      return `${type}-${database}-${schema || 'default'}-${itemName}`
    }
    if (schema) {
      return `${type}-${database}-${schema}`
    }
    if (database) {
      return `${type}-${database}`
    }
    return type
  }, [])

  // Context menu handling
  const handleContextMenu = useCallback((e: React.MouseEvent, type: string, database: string, schema?: string, name?: string, functionArguments?: string) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!_onContextMenuAction) return

    const itemId = getItemId(type, database, schema, name, functionArguments)
    
    // If right-clicking on a non-selected item, clear selection and select only this item
    if (!selectedItems.has(itemId)) {
      if (['database', 'schema', 'table', 'view', 'function', 'procedure', 'trigger', 'sequence', 'extension', 'index', 'event'].includes(type)) {
        setSelectedItems(new Set([itemId]))
        setLastSelectedId(itemId)
      } else {
        setSelectedItems(new Set())
        setLastSelectedId(null)
      }
    }

    let items: ContextMenuItem[] = []
    
    // If multiple items are selected and they're droppable types, show bulk operations
    if (selectedItems.size > 1) {
      const droppableTypes = ['database', 'schema', 'table', 'view', 'function', 'procedure', 'trigger', 'sequence', 'extension', 'index', 'event']
      const selectedTypes = Array.from(selectedItems, id => {
        const parts = id.split('-')
        return parts[0]
      })
      const hasDroppableItems = selectedTypes.some(t => droppableTypes.includes(t))
      
      if (hasDroppableItems) {
        items = buildMultiSelectContextMenu(selectedItems.size, (action) => {
          _onContextMenuAction(action, { selectedItems: Array.from(selectedItems) })
        }, selectedTypes)
      }
    }
    
    // Single item context menu
    if (items.length === 0) {
      switch (type) {
        case 'databases-root':
          items = buildDatabasesRootContextMenu(_onContextMenuAction)
          break
        case 'database':
          items = buildDatabaseContextMenu(database, _onContextMenuAction, dataSource.databaseType)
          break
        case 'schema':
          items = buildSchemaContextMenu(database, schema!, _onContextMenuAction)
          break
        case 'table':
          items = buildTableContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'view':
          items = buildViewContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'function':
          items = buildFunctionContextMenu(database, name!, schema, _onContextMenuAction, functionArguments)
          break
        case 'procedure':
          items = buildProcedureContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'index':
          items = buildIndexContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'trigger':
          items = buildTriggerContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'sequence':
          items = buildSequenceContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'event':
          items = buildEventContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'extension':
          items = buildExtensionContextMenu(database, name!, schema, _onContextMenuAction)
          break
        case 'folder':
          items = buildFolderContextMenu(name!, database, schema, _onContextMenuAction)
          break
      }
    }

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      items
    })
  }, [selectedItems, getItemId, _onContextMenuAction, dataSource.databaseType])

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }))
  }, [])

  // Memoized context menu handlers for performance
  const handleDatabaseContextMenu = useCallback((e: React.MouseEvent, database: string) => {
    handleContextMenu(e, 'database', database)
  }, [handleContextMenu])

  const handleSchemaContextMenu = useCallback((e: React.MouseEvent, database: string, schema: string) => {
    handleContextMenu(e, 'schema', database, schema)
  }, [handleContextMenu])

  const handleFolderContextMenu = useCallback((e: React.MouseEvent, database: string, schema: string | undefined, folderType: string) => {
    handleContextMenu(e, 'folder', database, schema, folderType)
  }, [handleContextMenu])


  if (isLoadingDatabases) {
    return (
      <div className="p-4">
        <div className="flex items-center text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border border-gray-300 border-t-transparent mr-2"></div>
          Loading databases...
        </div>
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="h-full overflow-auto" data-testid="database-tree">
      <div className="p-2">
        <div className="space-y-1">
          {/* Connection Root */}
          <div className="mb-2 group">
            <div 
              className="flex items-center justify-between py-1.5 px-2 text-sm font-medium text-gray-900 dark:text-white"
              onContextMenu={(e) => { handleContextMenu(e, 'databases-root', '') }}
            >
              <div className="flex items-center">
                <CircleStackIcon className="w-4 h-4 mr-2 text-blue-600" />
                <span className="truncate">Databases</span>
                <span className="ml-2 text-xs text-gray-400">
                  ({visibleDatabasesCount}{hasNextPage ? '+' : ''})
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <button type="button"
                  onClick={() => { setIsFilterDialogOpen(true) }}
                  className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100 transition-all"
                  title="Filter databases and schemas"
                >
                  <FunnelIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                </button>
                <button type="button"
                  onClick={handleRefresh}
                  className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100 transition-all"
                  title="Refresh databases"
                >
                  <ArrowPathIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Multi-selection indicator */}
          {selectedItems.size > 1 && (
            <div className="mb-2 mx-2 px-2 py-1 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-md">
              <div className="flex items-center justify-between text-xs text-blue-700 dark:text-blue-300">
                <div className="flex flex-col">
                  <span className="font-medium">{selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected</span>
                  <span className="text-blue-600 dark:text-blue-400 mt-1">
                    {(() => {
                      const types = Array.from(selectedItems, id => id.split('-')[0])
                      const typeCounts: Record<string, number> = {}
                      for (const type of types) {
                        typeCounts[type] = (typeCounts[type] || 0) + 1
                      }
                      
                      return Object.entries(typeCounts)
                        .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
                        .join(', ')
                    })()}
                  </span>
                </div>
                <button type="button"
                  onClick={() => {
                    setSelectedItems(new Set())
                    setLastSelectedId(null)
                  }}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium ml-2"
                >
                  Clear
                </button>
              </div>
              <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                💡 Ctrl+click to add/remove items, Shift+click for range selection, ESC to clear all
              </div>
            </div>
          )}
          
          {/* Databases */}
          {visibleDatabasesCount === 0 ? (
            <div className="py-2 px-4 text-sm text-gray-500">
              {databasesList.length === 0 ? 'No databases found' : 'All databases are filtered'}
            </div>
          ) : (
            <>
              {visibleDatabasesList
                .map((database) => (
                  <DatabaseNode key={database.name} database={database} />
                ))}
              
              {/* Loading indicator for pagination */}
              {isFetchingNextPage && (
                <div className="py-3 px-4 text-sm text-gray-500 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600 mr-2"></div>
                  Loading more databases...
                </div>
              )}
              
              {/* Scroll hint when there's more content */}
              {hasNextPage && !isFetchingNextPage && visibleDatabasesCount > 0 && (
                <div className="py-2 px-4 text-xs text-gray-400 text-center border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-center space-x-1">
                    <span>Scroll down for more</span>
                    <div className="animate-bounce">↓</div>
                  </div>
                </div>
              )}
              
              {/* End of list indicator */}
              {!hasNextPage && visibleDatabasesCount > 0 && (
                <div className="py-2 px-4 text-xs text-gray-400 text-center border-t border-gray-200 dark:border-gray-700">
                  {visibleDatabasesCount === databasesList.length 
                    ? `All databases loaded (${databasesList.length} total)`
                    : `Showing ${visibleDatabasesCount} of ${databasesList.length} databases (${databasesList.length - visibleDatabasesCount} filtered)`
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenu.items}
        onClose={closeContextMenu}
        visible={contextMenu.visible}
      />
      
      {/* DatabaseFilterDialog currently only needs connectionId and open state, onClose is handled via local state */}
      <DatabaseFilterDialog
        isOpen={isFilterDialogOpen}
        onClose={() => {
          setIsFilterDialogOpen(false)
          // Trigger a refresh to apply the new filters
          void refetchDatabases()
        }}
        connectionId={connectionId}
        dataSourceId={dataSource?.id || ''}
        databaseType={dbTypeForFilter}
        onApply={() => { /* no-op for now */ }}
      />
    </div>
  )
})
